export interface SessionTimeoutPolicy {
  idleTimeoutMs: number;
  warningDurationMs: number;
  absoluteTimeoutMs: number;
}

export type SessionExpiryReason = 'idle' | 'absolute';

export interface SessionTimeoutStatus {
  reason: SessionExpiryReason;
  remainingMs: number;
  shouldWarn: boolean;
  expired: boolean;
}

const DEFAULT_IDLE_TIMEOUT_MINUTES = 15;
const DEFAULT_WARNING_MINUTES = 2;
const DEFAULT_ABSOLUTE_TIMEOUT_HOURS = 8;
const SESSION_TRACKING_PREFIX = 'odinseye.auth.session.';

const readPositiveNumber = (value: string | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const createSessionTimeoutPolicy = (
  env: NodeJS.ProcessEnv = process.env,
): SessionTimeoutPolicy => {
  const idleTimeoutMs = readPositiveNumber(
    env.REACT_APP_SESSION_IDLE_TIMEOUT_MINUTES,
    DEFAULT_IDLE_TIMEOUT_MINUTES,
  ) * 60 * 1000;
  const configuredWarningMs = readPositiveNumber(
    env.REACT_APP_SESSION_WARNING_MINUTES,
    DEFAULT_WARNING_MINUTES,
  ) * 60 * 1000;

  return {
    idleTimeoutMs,
    warningDurationMs: Math.min(configuredWarningMs, idleTimeoutMs),
    absoluteTimeoutMs: readPositiveNumber(
      env.REACT_APP_SESSION_ABSOLUTE_TIMEOUT_HOURS,
      DEFAULT_ABSOLUTE_TIMEOUT_HOURS,
    ) * 60 * 60 * 1000,
  };
};

export const SESSION_TIMEOUT_POLICY = createSessionTimeoutPolicy();

export const evaluateSessionTimeout = (
  now: number,
  lastActivityAt: number,
  sessionStartedAt: number,
  policy: SessionTimeoutPolicy,
): SessionTimeoutStatus => {
  const idleRemainingMs = policy.idleTimeoutMs - (now - lastActivityAt);
  const absoluteRemainingMs = policy.absoluteTimeoutMs - (now - sessionStartedAt);
  const reason: SessionExpiryReason =
    absoluteRemainingMs <= idleRemainingMs ? 'absolute' : 'idle';
  const remainingMs = Math.min(idleRemainingMs, absoluteRemainingMs);

  return {
    reason,
    remainingMs,
    shouldWarn: remainingMs > 0 && remainingMs <= policy.warningDurationMs,
    expired: remainingMs <= 0,
  };
};

export const getSessionTrackingKeys = (sessionId: string) => ({
  lastActivity: `${SESSION_TRACKING_PREFIX}${sessionId}.lastActivity`,
  startedAt: `${SESSION_TRACKING_PREFIX}${sessionId}.startedAt`,
});

export const clearSessionTimeoutState = (): void => {
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(SESSION_TRACKING_PREFIX)) {
      localStorage.removeItem(key);
    }
  }
};

export const readStoredTimestamp = (key: string, fallback: number): number => {
  const parsed = Number(localStorage.getItem(key));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

